import { useFrame, useThree } from "@react-three/fiber";
import Cursor from "./Cursor";
import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { useAwarenessUpdate, useBroadcastUpdate, useMultiuserContext, useSynchronizedState } from "@solidwhite/multiuser";
import { throttle } from "lodash-es";
import { animated, useSpring } from "@react-spring/three";
import { useUsers } from "y-presence";

const identityVector = new THREE.Vector3();
const lastHit = new THREE.Vector3();
const cursorRaycaster = new THREE.Raycaster();
cursorRaycaster.layers.set(2);

const cursorpointer = new THREE.Vector2();

const PresentatorCursor = ({ ...props }) => {
  const { awareness, broadcastMessage } = useMultiuserContext();
  const { camera, scene } = useThree();

  const throttledSet = useMemo(() => {
    return throttle((value) => {
      broadcastMessage("pointer", value.toArray());
    }, 66);
  }, [awareness]);

  const onPointerMove = (event) => {
    // calculate pointer position in normalized device coordinates
    // (-1 to +1) for both components
    cursorpointer.x = (event.clientX / window.innerWidth) * 2 - 1;
    cursorpointer.y = -(event.clientY / window.innerHeight) * 2 + 1;
  };

  useEffect(() => {
    window.addEventListener("pointermove", onPointerMove);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
    };
  }, []);

  const [spring, api] = useSpring(() => ({
    position: [0, 0, 0],
    config: {
      duration: 16,
      tension: 380,
      friction: 30,
    },
  }));

  useFrame(() => {
    cursorRaycaster.setFromCamera(cursorpointer, camera);
    var hit = cursorRaycaster.intersectObject(scene, true);

    let target = hit.length > 0 ? hit[0].point : lastHit;

    api.start({
      position: target.toArray(),
    });
    throttledSet(target);
  }, 2);

  return (
    <animated.group position={spring.position} scale={1}>
      <Cursor show />
    </animated.group>
  );
};

export const RemotePresentatorCursor = () => {
  const { awareness } = useMultiuserContext();

  const presentatorId = useMemo(() => {
    let id = null;
    awareness.states.forEach((v, k) => {
      if (v.presentator) {
        id = k;
      }
    });
    return id;
  }, []);

  useBroadcastUpdate(presentatorId, "pointer", (e) => {
    if (!e) return;
    api.start({ position: e });
  });

  const user = useUsers(
    awareness,
    (state) => {
      return state.get(presentatorId);
    },
    (a, b) => {
      return a?.name == b?.name;
    },
  );

  const [spring, api] = useSpring(() => ({
    position: [0, 0, 0],
    config: {
      tension: 280,
      friction: 30,
    },
  }));

  if (!user) return null;

  return (
    <animated.group position={spring.position} scale={1}>
      <Cursor text={user.name} />
    </animated.group>
  );
};

export default PresentatorCursor;
